import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Save, AlertCircle, Check, Loader2, FileText, RefreshCw, Crown, User, ChevronDown, LogOut, Mic } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import toast from 'react-hot-toast';

const VoicePromptManagement = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState('SOC2_TYPE1');
  const [promptText, setPromptText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null);

  const backendUrl = '';
  const API = `${backendUrl}/api`;

  const frameworks = [
    { id: 'SOC2_TYPE1', name: 'SOC 2 Type 1', color: 'bg-blue-100 text-blue-700' },
    { id: 'SOC2_TYPE2', name: 'SOC 2 Type 2', color: 'bg-blue-100 text-blue-700' },
    { id: 'GDPR', name: 'GDPR', color: 'bg-green-100 text-green-700' },
    { id: 'HIPAA', name: 'HIPAA', color: 'bg-purple-100 text-purple-700' },
    { id: 'ISO27001', name: 'ISO 27001', color: 'bg-indigo-100 text-indigo-700' },
    { id: 'PCI_DSS', name: 'PCI DSS', color: 'bg-orange-100 text-orange-700' }
  ];

  useEffect(() => {
    fetchPrompts();
  }, []);

  useEffect(() => {
    if (prompts.length > 0) {
      loadPromptForFramework(selectedFramework);
    }
  }, [selectedFramework, prompts]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/voice-prompts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setPrompts(response.data.prompts || []);
      
      if (response.data.prompts && response.data.prompts.length > 0) {
        loadPromptForFramework(selectedFramework);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
      toast.error('Failed to fetch prompts');
    } finally {
      setLoading(false);
    }
  };

  const loadPromptForFramework = (framework) => {
    const prompt = prompts.find(p => p.framework === framework && p.is_active);
    if (prompt) {
      setPromptText(prompt.prompt_text);
      setCurrentVersion(prompt.version); // Track active version
      setHasChanges(false);
    } else {
      setPromptText('');
      setCurrentVersion(null);
      setHasChanges(false);
    }
  };

  const handlePromptChange = (value) => {
    setPromptText(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!promptText.trim()) {
      toast.error('Prompt cannot be empty');
      return;
    }

    try {
      setSaving(true);
      
      // Let the backend calculate the correct next version number
      await axios.put(
        `${API}/admin/voice-prompts/${selectedFramework}`,
        {
          framework: selectedFramework,
          prompt_text: promptText,
          is_active: true
          // Don't send version - let backend calculate it
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success('Prompt saved successfully!');
      setHasChanges(false);
      fetchPrompts(); // Refresh prompts
      loadPromptForFramework(selectedFramework); // Reload to get new version number
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error('Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleInitialize = async () => {
    if (prompts.length > 0) {
      if (!window.confirm('Prompts already exist. This will not override them. Continue?')) {
        return;
      }
    }

    try {
      setInitializing(true);
      const response = await axios.post(
        `${API}/admin/voice-prompts/initialize`,
        {},
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success(response.data.message || 'Prompts initialized successfully!');
      fetchPrompts(); // Refresh prompts
    } catch (error) {
      console.error('Error initializing prompts:', error);
      toast.error(error.response?.data?.detail || 'Failed to initialize prompts');
    } finally {
      setInitializing(false);
    }
  };

  const fetchVersionHistory = async (framework) => {
    try {
      setLoadingVersions(true);
      const response = await axios.get(`${API}/admin/voice-prompts/${framework}/versions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setVersionHistory(response.data.versions || []);
      setShowVersionHistory(true);
    } catch (error) {
      console.error('Error fetching version history:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  const activateVersion = async (framework, version) => {
    // Set up the revert confirmation modal
    setRevertTarget({ framework, version });
    setShowRevertConfirm(true);
  };

  const confirmRevert = async () => {
    if (!revertTarget) return;

    const { framework, version } = revertTarget;

    try {
      const response = await axios.post(
        `${API}/admin/voice-prompts/${framework}/activate/${version}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast.success(`Successfully reverted to version ${version}!`);
      fetchPrompts(); // Refresh to get the new active version
      fetchVersionHistory(framework); // Refresh version history
      loadPromptForFramework(framework); // Reload the prompt text
      setShowRevertConfirm(false);
      setRevertTarget(null);
    } catch (error) {
      console.error('Error activating version:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate version');
    }
  };

  const cancelRevert = () => {
    setShowRevertConfirm(false);
    setRevertTarget(null);
  };

  const viewVersionDetails = async (framework, version) => {
    try {
      const response = await axios.get(`${API}/admin/voice-prompts/${framework}/version/${version}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setPromptText(response.data.prompt_text);
      setCurrentVersion(version);
      setHasChanges(false);
      toast.info(`Viewing version ${version}`, { duration: 3000 });
    } catch (error) {
      console.error('Error loading version:', error);
      toast.error('Failed to load version details');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
      </div>
    );
  }

  const selectedFrameworkData = frameworks.find(f => f.id === selectedFramework);

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Voice Agent System Prompts</h1>
                  <p className="text-sm text-slate-600">Manage and customize Nova's behavior for each framework</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {prompts.length === 0 && (
                <Button
                  onClick={handleInitialize}
                  disabled={initializing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {initializing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Initializing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Initialize Prompts
                    </>
                  )}
                </Button>
              )}
              
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {prompts.length === 0 && (
          <Card className="mb-6 border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">No Prompts Found</h3>
                  <p className="text-sm text-amber-800 mb-3">
                    Click "Initialize Prompts" to load default system prompts from the codebase into the database.
                    After initialization, you can customize each prompt for your needs.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Framework Selection Sidebar */}
          <div className="col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Framework Selection</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {frameworks.map(framework => {
                  const hasPrompt = prompts.find(p => p.framework === framework.id && p.is_active);
                  return (
                    <button
                      key={framework.id}
                      onClick={() => {
                        if (hasChanges) {
                          if (window.confirm('You have unsaved changes. Discard them?')) {
                            setSelectedFramework(framework.id);
                          }
                        } else {
                          setSelectedFramework(framework.id);
                        }
                      }}
                      className={`w-full text-left p-3 rounded-lg transition-all ${
                        selectedFramework === framework.id
                          ? 'bg-navy-600 text-white shadow-lg'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{framework.name}</span>
                        {hasPrompt && (
                          <Check className="w-4 h-4 text-green-400" />
                        )}
                      </div>
                      {hasPrompt && (
                        <span className="text-xs opacity-75 mt-1 block">
                          v{hasPrompt.version}
                        </span>
                      )}
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Prompt Editor */}
          <div className="col-span-9">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-navy-600" />
                      {selectedFrameworkData?.name} Prompt Template
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1">
                      This prompt defines how Nova conducts assessments for {selectedFrameworkData?.name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => fetchVersionHistory(selectedFramework)}
                      variant="outline"
                      size="sm"
                      disabled={loadingVersions}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingVersions ? 'animate-spin' : ''}`} />
                      Version History
                    </Button>
                    {hasChanges && (
                      <Badge className="bg-amber-100 text-amber-700">Unsaved Changes</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Version Indicator Banner */}
                {currentVersion && (
                  <div className={`mb-4 p-3 rounded-lg border-2 flex items-center justify-between ${
                    prompts.find(p => p.framework === selectedFramework && p.is_active)?.version === currentVersion
                      ? 'bg-green-50 border-green-300'
                      : 'bg-blue-50 border-blue-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-700" />
                      <div>
                        <p className="font-semibold text-slate-900">
                          Viewing Version {currentVersion}
                        </p>
                        <p className="text-sm text-slate-600">
                          {prompts.find(p => p.framework === selectedFramework && p.is_active)?.version === currentVersion
                            ? 'This is the active version currently in use'
                            : 'This is a historical version - not currently active'}
                        </p>
                      </div>
                    </div>
                    <Badge className={
                      prompts.find(p => p.framework === selectedFramework && p.is_active)?.version === currentVersion
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }>
                      {prompts.find(p => p.framework === selectedFramework && p.is_active)?.version === currentVersion
                        ? 'Active'
                        : 'Historical'}
                    </Badge>
                  </div>
                )}
                
                <textarea
                  value={promptText}
                  onChange={(e) => handlePromptChange(e.target.value)}
                  className="w-full h-96 p-4 border border-slate-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                  placeholder="Enter the system prompt for this framework..."
                />

                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-slate-500">
                    {promptText.length} characters
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => loadPromptForFramework(selectedFramework)}
                      variant="outline"
                      disabled={!hasChanges}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving || !hasChanges || !promptText.trim()}
                      className="bg-navy-600 hover:bg-navy-700"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Prompt
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-sm">Prompt Tips</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-600 space-y-2">
                <ul className="list-disc list-inside space-y-1">
                  <li>Use <code className="bg-slate-100 px-1 rounded">{'{framework_display}'}</code> to insert the framework name dynamically</li>
                  <li>Define the AI's persona, tone, and conversation style clearly</li>
                  <li>List all domains/areas to be covered in the assessment</li>
                  <li>Specify how to structure the final summary output</li>
                  <li>Include examples of good questioning patterns</li>
                  <li>Changes take effect immediately for new assessments</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Version History Modal */}
      {showVersionHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-[800px] max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle>Version History - {selectedFrameworkData?.name}</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVersionHistory(false)}
                >
                  Close
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto p-6">
              {loadingVersions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-navy-600" />
                </div>
              ) : versionHistory.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No versions found</p>
              ) : (
                <div className="space-y-3">
                  {versionHistory.map((version) => (
                    <div
                      key={version.version}
                      className={`p-4 border-2 rounded-lg ${
                        version.is_active
                          ? 'border-green-500 bg-green-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-slate-900">
                              Version {version.version}
                            </h4>
                            {version.is_active && (
                              <Badge className="bg-green-100 text-green-700">Active</Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-600 space-y-1">
                            <p>
                              <span className="font-medium">Updated:</span>{' '}
                              {new Date(version.updated_at).toLocaleString()}
                            </p>
                            <p>
                              <span className="font-medium">By:</span> {version.updated_by}
                            </p>
                            <p className="text-xs text-slate-500 mt-2">
                              {version.prompt_text_preview}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              viewVersionDetails(selectedFramework, version.version);
                              setShowVersionHistory(false);
                            }}
                          >
                            View
                          </Button>
                          {!version.is_active && (
                            <Button
                              size="sm"
                              onClick={() => activateVersion(selectedFramework, version.version)}
                              className="bg-navy-600 hover:bg-navy-700"
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Revert
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revert Confirmation Modal */}
      {showRevertConfirm && revertTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-[600px] max-w-[90vw]">
            <CardHeader className="border-b">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-slate-900">
                    Confirm Version Revert
                  </CardTitle>
                  <p className="text-sm text-slate-600 mt-1">
                    This action will change the active system prompt
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Framework:</p>
                      <p className="text-sm text-slate-900">
                        {frameworks.find(f => f.id === revertTarget.framework)?.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Target Version:</p>
                      <p className="text-sm text-slate-900">Version {revertTarget.version}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Current Active:</p>
                      <p className="text-sm text-slate-900">
                        Version {prompts.find(p => p.framework === revertTarget.framework && p.is_active)?.version || 'Unknown'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Last Updated:</p>
                      <p className="text-sm text-slate-900">
                        {versionHistory.find(v => v.version === revertTarget.version)?.updated_at
                          ? new Date(versionHistory.find(v => v.version === revertTarget.version).updated_at).toLocaleString()
                          : 'Unknown'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-amber-800">Important:</p>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• This will immediately activate Version {revertTarget.version}</li>
                        <li>• All new voice assessments will use the reverted prompt</li>
                        <li>• The current active version will be deactivated but preserved</li>
                        <li>• This action can be undone by reverting back to another version</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Preview:</strong> 
                    {versionHistory.find(v => v.version === revertTarget.version)?.prompt_text_preview || 'Loading...'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={cancelRevert}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmRevert}
                  className="bg-amber-600 hover:bg-amber-700 px-6"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Yes, Revert to Version {revertTarget.version}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default VoicePromptManagement;
